import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getCartItems, addToCart as apiAddToCart, updateCart as apiUpdateCart, removeFromCart as apiRemoveFromCart } from "../api/api";
import toast from "react-hot-toast";

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const toggleCart = () => setIsCartOpen(!isCartOpen);

  const getUserId = () => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      return user?.id || null;
    }
    return null;
  };

  const fetchCart = useCallback(async () => {
    const userId = getUserId();
    if (!userId) {
      setCart([]);
      return;
    }
    
    setLoading(true);
    try {
      const res = await getCartItems(userId);
      // Ensure we always have an array
      if (res.data && Array.isArray(res.data)) {
        setCart(res.data);
      } else if (res.data && res.data.cart) {
        setCart(res.data.cart);
      } else {
        setCart([]);
      }
    } catch (error) {
      console.error("Failed to fetch cart", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (foodItem, quantity = 1) => {
    const userId = getUserId();
    if (!userId) {
      toast.error("Please login to add to cart");
      return false;
    }

    // Optimistic UI update
    const existingItem = cart.find(item => item.food_id === foodItem.id || item.id === foodItem.id);
    let newCart = [...cart];
    
    if (existingItem) {
      newCart = newCart.map(item => 
        (item.food_id === foodItem.id || item.id === foodItem.id) 
          ? { ...item, quantity: parseInt(item.quantity) + quantity }
          : item
      );
    } else {
      newCart.push({
        id: foodItem.id,
        food_id: foodItem.id,
        name: foodItem.name,
        price: foodItem.price,
        image: foodItem.image,
        quantity: quantity
      });
    }
    setCart(newCart);

    // Backend sync
    try {
      const res = await apiAddToCart({ user_id: userId, food_id: foodItem.id, quantity });
      if (res?.data?.error) throw new Error(res.data.error);
      toast.success("🛒 Added to cart!");
      
      // We MUST fetch cart after adding so we get the real database cart_id for future updates
      await fetchCart();
      return true;
    } catch (error) {
      console.error("Add to cart error", error);
      toast.error("Failed to add to cart");
      // Revert on error
      fetchCart();
      return false;
    }
  };

  const updateQuantity = async (foodId, newQuantity) => {
    const userId = getUserId();
    if (!userId) return;

    if (newQuantity <= 0) {
      return removeFromCart(foodId);
    }

    // Find the real cart_id
    const itemInCart = cart.find(item => item.food_id === foodId || item.id === foodId);
    if (!itemInCart) return;
    const cart_id = itemInCart.id; // from get_cart.php, c.id is id

    // Optimistic update
    setCart(prev => prev.map(item => 
      (item.food_id === foodId || item.id === foodId) ? { ...item, quantity: newQuantity } : item
    ));

    try {
      const res = await apiUpdateCart({ cart_id, quantity: newQuantity });
      if (res?.data?.error) throw new Error(res.data.error);
    } catch (error) {
      console.error("Update cart error", error);
      fetchCart(); // Revert
    }
  };

  const removeFromCart = async (foodId) => {
    const userId = getUserId();
    if (!userId) return;

    // Find the real cart_id
    const itemInCart = cart.find(item => item.food_id === foodId || item.id === foodId);
    if (!itemInCart) return;
    const cart_id = itemInCart.id;

    // Optimistic update
    setCart(prev => prev.filter(item => item.food_id !== foodId && item.id !== foodId));

    try {
      const res = await apiRemoveFromCart({ cart_id });
      if (res?.data?.error) throw new Error(res.data.error);
      toast.success("Item removed");
    } catch (error) {
      console.error("Remove from cart error", error);
      toast.error("Failed to remove item");
      fetchCart(); // Revert
    }
  };

  const clearCartLocally = () => {
    setCart([]);
  };

  const cartTotal = cart.reduce((total, item) => total + (parseFloat(item.price) * parseInt(item.quantity)), 0);
  const cartCount = cart.reduce((count, item) => count + parseInt(item.quantity), 0);

  return (
    <CartContext.Provider value={{ 
      cart, 
      loading, 
      addToCart, 
      updateQuantity, 
      removeFromCart, 
      cartTotal, 
      cartCount,
      fetchCart,
      clearCartLocally,
      isCartOpen,
      setIsCartOpen,
      toggleCart
    }}>
      {children}
    </CartContext.Provider>
  );
};
